import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { convertUrlToEmbedUrl } from '@social-embed/lib';

import FieldDefault from 'components/field-default';

const PARTNER_MATCHERS = [
    {
        id: 'miro',
        regex: /^https:\/\/miro\.com\/app\/board\/.+/,
        replacer: url => {
            return url.replace(/^(https:\/\/miro\.com\/app)\/board\//, '$1/live-embed/')
        },
        title: 'Miro Board'
    },
    {
        id: 'gdriveFolder',
        regex: /^https:\/\/drive\.google\.com\/drive(?:\/.+)?\/folders\/.+?(?:\?.*)?$/,
        replacer: url => {
            return url.replace(/^https:\/\/drive\.google\.com\/drive(?:\/.+)?\/folders\/(.+?)(?:\?.*)?$/, 'https://drive.google.com/embeddedfolderview?id=$1#grid');
        },
        title: 'GDrive Folder'
    },
    {
        id: 'figma',
        regex: /^https:\/\/www\.figma\.com\/file\/.+/,
        replacer: url => {
            return`https://www.figma.com/embed?embed_host=share&url=${decodeURIComponent(url)}`
        },
        title: 'Figma Prototype'
    },
    {
        id: 'boxFolder',
        regex: /^https:\/\/app\.box\.com\/folder\/.+?\?s=.+/,
        replacer: url => {
            return url.replace(/^https:\/\/app\.box\.com\/folder\/.+?\?s=(.+)/, 'https://app.box.com/embed/s/$1');
        },
        title: 'Box Folder'
    },
    {
        id: 'boxFile',
        regex: /^https:\/\/app\.box\.com\/file\/.+?\?s=.+/,
        replacer: url => {
            return url.replace(/^https:\/\/app\.box\.com\/file\/.+?\?s=(.+)/, 'https://app.box.com/embed/s/$1');
        },
        title: 'Box File'
    },
    {
        id: 'airtable',
        regex: /^https:\/\/airtable\.com\/.+/,
        replacer: url => {
            return url.replace(/^https:\/\/airtable\.com\/(.+)/, 'https://airtable.com/embed/$1?backgroundColor=green&viewControls=on');
        },
        title: 'Airtable'
    },
    {
        id: 'twitch',
        regex: /^https:\/\/www\.twitch\.tv\/.+/,
        replacer: url => {
            return url.replace(/^https:\/\/www\.twitch\.tv\/(.+)/, 'https://player.twitch.tv/?channel=$1&parent=$HOSTNAME$');
        },
        title: 'Twitch Channel'
    },
    {
        id: 'dropbox',
        regex: /^https:\/\/www\.dropbox\.com\/.+?(\?.*?)$/,
        replacer: url => {
            return url.replace(/(^https:\/\/www\.dropbox\.com\/.+?)(\?.*?)$/, '$1?raw=1');
        },
        title: 'Dropbox File'
    }
];

const matchPartner = url => {
    const partner = PARTNER_MATCHERS.find(partner => url.match(partner.regex));

    if (!partner) {
        return null;
    }

    return partner;
}

const EmbedInput = ({ value, small, autoFocus, withLabel, placeholder, changeOnBlur, onChange, onPartnerMatch, provideController }) => {
    const { t } = useTranslation(['embed', 'common']);

    const handleChange = useCallback(input => {
        if (!changeOnBlur) {
            onChange(input);
        }
    }, [changeOnBlur, onChange]);

    const handleBlur = useCallback(input => {
        if (changeOnBlur) {
            onChange(input);
        }
    }, [changeOnBlur, onChange]);

    const handleUrlPaste = useCallback(event => {
        const clipboardData = event.clipboardData || window.clipboardData;
        const pastedData = clipboardData.getData('Text');
        const iframeSrcMatch = pastedData.match(/<iframe.+src=(?:'|")(.+?)(?:'|")/);
        let embedUrl = pastedData;

        if (iframeSrcMatch && iframeSrcMatch[1]) {
            embedUrl = iframeSrcMatch[1];
        }

        const partner = matchPartner(embedUrl);

        if (partner) {
            event.preventDefault();
            onChange(partner.replacer(embedUrl));
            onPartnerMatch && onPartnerMatch(partner)
        } else {
            const parsedEmbedUrl = convertUrlToEmbedUrl(embedUrl) || embedUrl;
            if (parsedEmbedUrl && parsedEmbedUrl !== pastedData) {
                event.preventDefault();
                onChange(parsedEmbedUrl);
            }
        }
    }, [onChange, onPartnerMatch]);

    return <FieldDefault
        id="embed-input"
        value={value}
        small={small}
        autoFocus={autoFocus}
        label={ withLabel ? t('common:URL') : null }
        placeholder={ t('createURLPlaceholder') }
        onChange={handleChange}
        onBlur={handleBlur}
        onPaste={handleUrlPaste}
        provideController={provideController}
    />
}

export default EmbedInput;
